import { useEffect } from 'react'
import { useRouter } from 'next/router'
import { omit } from 'ramda'

import { useNearestCities } from '@/lib/locations'
import { getSearch, pushSearch } from '@/utils/location'
import { stringify } from '@/utils/qs'

const useSyncMapBoundsToURL = ({
  bounds,
  justLoadedRef,
  setCityChanged,
  cityChanged,
  citySlug
}) => {
  const router = useRouter()
  const [getNearestCities] = useNearestCities()

  useEffect(() => {
    // Handle updating URL to include box param and/or city slug when bounds are changed
    const updateSlugAndBox = async () => {
      if (
        justLoadedRef.current ||
        cityChanged ||
        !bounds?.topLeft?.lon ||
        !bounds?.topLeft?.lat ||
        !bounds?.bottomRight?.lon ||
        !bounds?.bottomRight?.lat
      ) {
        return
      }

      const boxParam = `${bounds.topLeft.lon},${bounds.topLeft.lat},${bounds.bottomRight.lon},${bounds.bottomRight.lat}`

      const centerLat = (bounds.topLeft.lat + bounds.bottomRight.lat) / 2
      const centerLon = (bounds.topLeft.lon + bounds.bottomRight.lon) / 2

      const { data } = await getNearestCities({
        variables: {
          input: {
            lat: centerLat,
            lon: centerLon,
            size: 1
          }
        }
      })
      const newCitySlug = data?.nearestCities?.[0]?.slug

      if (!newCitySlug || newCitySlug === citySlug) {
        pushSearch(router, { box: boxParam, page: null })
        return
      }

      const search = omit(['page'], getSearch(router))
      delete search.cities

      const newSearch = {
        ...search,
        box: boxParam
      }

      const searchString = stringify(newSearch)
      const url = `/rental-listings/city/${newCitySlug}`
      setCityChanged(true)

      router.push(
        `${url}${searchString ? `?${searchString}` : ''}`,
        undefined,
        { shallow: true }
      )
    }

    updateSlugAndBox()
  }, [bounds])
}

export default useSyncMapBoundsToURL
